import { appConstants } from './appConstants';
import { bffRoutes } from './configuration';
import { OK, CREATED, INTERNAL_SERVER_ERROR } from 'http-status-codes';
import { checkStatus } from './authFunctions';
import { logger } from './logger/LoggerContext';

export const getToken = (
  businessUnit,
  formName,
  flow,
  setIsLoading,
  setIsError,
  setIsSubmitted,
  lang,
  setPdfUrl,
  setIsSubmitting,
) =>
  getJwtToken(businessUnit, formName, flow, setIsError).then((responseData) => {
    const loggingContext = `BusinessUnit: ${businessUnit}, FormName: ${formName}, Flow: ${flow}, Lang: ${lang}`;
    logger.info(`getToken: Received response for ${loggingContext}`);
    if (responseData && responseData.token && responseData.correlationId) {
      loadForm(
        responseData.token,
        responseData.correlationId,
        businessUnit,
        formName,
        flow,
        setIsLoading,
        setIsError,
        setIsSubmitted,
        lang,
        setPdfUrl,
        loggingContext,
        setIsSubmitting,
      );
    } else {
      logger.error(`getToken: Cannot load form - missing token or correlationId for ${loggingContext}`);
      setIsError(true);
    }
  });

/**
 * Loads the form from Daylight cloud and save the state and documents
 * @param {String} secret
 * @param {String} correlationId
 * @param {String} businessUnit
 * @param {String} formName
 * @param {String} flow
 * @param {Function} setIsLoading
 * @param {Function} setIsError
 * @param {Function} setIsSubmitted
 * @param {String} lang
 * @param {Function} setPdfUrl
 * @param {String} loggingContext
 * @param {Function} setIsSubmitting
 * @returns {Promise<String>} Status of document retrieval
 */
const loadForm = (
  secret,
  correlationId,
  businessUnit,
  formName,
  flow,
  setIsLoading,
  setIsError,
  setIsSubmitted,
  lang,
  setPdfUrl,
  loggingContext,
  setIsSubmitting,
) => {
  const language = lang === 'fr-CA' ? 'fr' : 'en';
  window.formhero.setFormHeroHost(appConstants.FORMHERO_HOST);
  window.formhero.useHttps(true);
  let statusUpdateResp = null;
  window.CWC_MLCA &&
    window.CWC_MLCA.dataLayer.push({
      event: 'form-initiated',
      form: {
        name: formName,
        language: language,
        id: correlationId.split(' ')[1],
        team: businessUnit,
      },
    });

  return window.formhero
    .loadForm(
      {
        onStatusFn(status) {
          if (flow === appConstants.FLOW_AUTH) {
            statusUpdate(status, setIsError, flow, formName, loggingContext);
          }

          if (flow === appConstants.FLOW_UNAUTH && status.state === 'submitted' && status.formHeroSessionId) {
            statusUpdateResp = { status, setIsError, flow };
          }
          //this will fired only when form is loaded on the screen and progress completion is not a type [Number]
          if (status.progress.completionPercentage === undefined) {
            setIsLoading(false);
          }
          if (status.formError) {
            logger.warn(`loadForm: Form Error. Reason: ${status.formError}`);
          }
        },
        signedRequest: secret,
        lang: language,
      },
      {
        'FORMHERO.correlationId': correlationId.split(' ')[1],
      },
    )
    .then(async function (successResult) {
      setIsSubmitting(true);
      logger.info(`loadForm: Form submitted successfully. State: ${successResult.state}, ${loggingContext}`);
      if (null !== statusUpdateResp) {
        await statusUpdate(
          statusUpdateResp.status,
          statusUpdateResp.setIsError,
          statusUpdateResp.flow,
          formName,
          loggingContext,
        );
      }
      window.CWC_MLCA &&
        window.CWC_MLCA.dataLayer.push({
          event: 'form-submit-success',
          form: {
            name: formName,
            language: language,
            id: correlationId.split(' ')[1],
            team: businessUnit,
          },
        });
      logger.info(`loadForm: Starting document retrieval for ${loggingContext}`);
      const status = await documentRetrieve(successResult, flow, setIsError, setPdfUrl, formName, loggingContext);
      logger.info(`loadForm: Document retrieval completed with status: ${status} for ${loggingContext}`);
      setIsSubmitting(false);

      if (status === OK) {
        setIsSubmitted(true);
      } else {
        setIsError(true);
      }
    })
    .catch((error) => {
      logger.error(`loadForm: Form submission failed for ${loggingContext}. Reason: ${error?.message}`);
      setIsSubmitting(false);
      setIsError(true);
    });
};

/**
 * Get JWT token from BFF
 * @param {String} businessUnit
 * @param {String} formName
 * @param {String} flow
 * @param {Function} setIsError
 * @returns {Promise<String>} JWT token
 */
const getJwtToken = (businessUnit, formName, flow, setIsError) => {
  const requestBody = JSON.stringify({
    businessUnit,
    formName,
    flow,
  });
  return fetch(bffRoutes.getTokenGenerate(), {
    mode: 'cors',
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': appConstants.ACCEPT_TYPE,
      Accept: appConstants.ACCEPT_TYPE,
    },
    body: requestBody,
  })
    .then(checkStatus)
    .then((response) => response.json())
    .then((responseData) => responseData)
    .catch((error) => {
      logger.error(
        `getJwtToken: Failed to get JWT token for BusinessUnit: ${businessUnit}, FormName: ${formName}, Flow: ${flow}, Reason: ${error?.message}`,
      );
      setIsError(true);
    });
};

/**
 * Sends request to save form data in db
 * @param {Object} formData collected data from daylight form
 * @param {Function} setIsError function to update isError state
 * @param {String} flow authorized or unauthorized
 * @param {String} formName form name from the URL
 * @param {String} loggingContext context for logging
 * @returns Success or null
 */
const updateData = async (formData, setIsError, flow, formName, loggingContext) => {
  logger.info(`updateData: Saving form state for ${loggingContext}`);
  const requestBody = JSON.stringify({
    formData,
    flow,
    formName,
  });
  return fetch(bffRoutes.getTokenUpdate(), {
    credentials: 'include',
    mode: 'cors',
    method: 'POST',
    headers: {
      'Content-Type': appConstants.ACCEPT_TYPE,
      Accept: appConstants.ACCEPT_TYPE,
    },
    body: requestBody,
  })
    .then(checkStatus)
    .then((response) => {
      if (response.status === OK || response.status === CREATED) {
        logger.info(`updateData: Successfully saved form state for ${loggingContext}. Status: ${response.status}`);
        return 'Success';
      } else {
        logger.error(`updateData: Unexpected status for ${loggingContext}. Status: ${response.status}`);
        setIsError(true);
        return null;
      }
    })
    .catch((error) => {
      logger.error(`updateData: Failed to save form state for ${loggingContext}. Error: ${error?.message}`);
      setIsError(true);
    });
};

/**
 * Update the form data status in DB
 * @param {String} status
 * @param {Function} setIsError
 * @param {String} flow
 * @param {String} formName
 * @param {String} loggingContext
 * @returns {Promise<String|null>} Status of the update or null
 */
export const statusUpdate = (status, setIsError, flow, formName, loggingContext) => {
  if (status && status.formHeroSessionId) {
    return updateData(status.collectedData, setIsError, flow, formName, loggingContext);
  }
  return null;
};

/**
 * Retrieves the submitted form documents and send request to save documents in DB
 * @param {Object} successResult
 * @param {String} flow
 * @param {Function} setIsError
 * @param {Function} setPdfUrl
 * @param {String} formName
 * @param {String} loggingContext
 * @returns {Promise<String>} Status of document retrieval
 */
export const documentRetrieve = async (successResult, flow, setIsError, setPdfUrl, formName, loggingContext) => {
  try {
    let uploads = [];
    logger.info(`documentRetrieve: Starting for ${loggingContext}`);

    if (successResult.state === 'form-success') {
      uploads = uploads.concat(
        successResult.artifacts.map(async (item) => {
          if (
            item.fileName.startsWith(bffRoutes.getPdfNameCheckEnglish()) ||
            item.fileName.startsWith(bffRoutes.getPdfNameCheckFrench())
          ) {
            setPdfUrl(await getDocumentPdf(item.url, item.fileName, setIsError));
          }
          return uploadDocuments(item.url, item.fileName, setIsError, item.type);
        }),
      );
    } else {
      logger.warn(`documentRetrieve: Unexpected state for ${loggingContext}. State: ${successResult.state}`);
    }

    logger.info(`documentRetrieve: Waiting for ${uploads.length} document uploads for ${loggingContext}`);
    const newV = await Promise.all(uploads);
    logger.info(`documentRetrieve: All documents uploaded for ${loggingContext}. Calling saveDocuments...`);

    return saveDocuments(newV, flow, successResult.collectedData, setIsError, formName, loggingContext);
  } catch (error) {
    logger.error(`documentRetrieve: Failed for ${loggingContext}. Error: ${error?.message}`);
    setIsError(true);
    return INTERNAL_SERVER_ERROR;
  }
};

/**
 * Retrieves a PDF document from the given URL
 * @param {String} url
 * @param {String} fileName
 * @param {Function} setIsError
 * @returns {Promise<String|Blob>} PDF document as a Blob or URL
 */
export const getDocumentPdf = async (url, fileName, setIsError) => {
  logger.info(`getDocumentPdf: Fetching PDF document: ${fileName}`);
  return fetch(url, {
    method: 'GET',
  })
    .then(checkStatus)
    .then(async (response) => {
      const body = await response.blob();
      const file = new Blob([body], { type: 'application/pdf' });

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        logger.info(`getDocumentPdf: Successfully retrieved PDF document: ${fileName}`);
        return file;
      } else {
        const fileURL = URL.createObjectURL(file);
        logger.info(`getDocumentPdf: Successfully created PDF document URL`);
        return fileURL;
      }
    })
    .catch((error) => {
      logger.error(`getDocumentPdf: Failed to fetch PDF document: ${fileName}. Error: ${error?.message}`);
      setIsError(true);
    });
};

/**
 * Uploads documents to DB
 * @param {String} url
 * @param {String} fileName
 * @param {Function} setIsError
 * @param {String} fileType
 * @returns {Promise<Object>} Uploaded document data
 */
const uploadDocuments = async (url, fileName, setIsError, fileType) => {
  logger.info(`uploadDocuments: Fetching document: ${fileName}`);
  return fetch(url, {
    method: 'GET',
  })
    .then(checkStatus)
    .then(async (response) => {
      logger.info(`uploadDocuments: Converting ${fileName} to base64`);
      return {
        id: fileName,
        data: await blob2b64(await response.blob(), setIsError, fileName),
        fileType,
      };
    })
    .catch((error) => {
      logger.error(`uploadDocuments: Failed to upload ${fileName}. Error: ${error?.message}`);
      setIsError(true);
    });
};

/**
 * Converts a Blob to a base64 encoded string
 * @param {Blob} blob
 * @param {function} setIsError
 * @param {String} fileName
 * @returns {Promise<String>} Base64 encoded string
 */
export const blob2b64 = (blob, setIsError, fileName) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        reader.readAsArrayBuffer(blob);
        reader.onloadend = function () {
          let binary = '';
          const bytes = new Uint8Array(reader.result);
          const length = bytes.byteLength;
          for (let i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64data = btoa(binary);
          resolve(base64data);
        };
      } else {
        reader.readAsBinaryString(blob);
        reader.onloadend = function () {
          const base64data = btoa(reader.result);
          resolve(base64data);
        };
      }
    } catch (e) {
      logger.error(`blob2b64: Conversion failed for ${fileName}. Error: ${e?.message}`);
      setIsError(true);
      reject(e);
    }
  });
};

/**
 * Saves uploaded documents to DB
 * @param {Object} uploads
 * @param {String} flow
 * @param {Object} formData
 * @param {function} setIsError
 * @param {String} formName
 * @param {String} loggingContext
 * @returns {Promise<Number>} Status of save documents
 */
const saveDocuments = async (uploads, flow, formData, setIsError, formName, loggingContext) => {
  const payload = JSON.stringify({
    uploads,
    flow,
    formData,
    formName,
  });
  return fetch(bffRoutes.getSaveDocuments(), {
    mode: 'cors',
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': appConstants.ACCEPT_TYPE,
      Accept: appConstants.ACCEPT_TYPE,
    },
    body: payload,
  })
    .then(checkStatus)
    .then((response) => {
      logger.info(`saveDocuments: Documents saved successfully for ${loggingContext}. Status: ${response.status}`);
      return response.status;
    })
    .catch((error) => {
      logger.error(`saveDocuments: Failed to save documents for ${loggingContext}. Error: ${error?.message}`);
      setIsError(true);
      return INTERNAL_SERVER_ERROR;
    });
};
